import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Schema } from "@/shared/components/Form/types";
import { QuickFilter } from "../CRUD/QuickFilter";
import { CustomToolbar } from "./CustomToolbar";
import { CustomToolbarSelect } from "./CustomToolbarSelect";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { downloadAsCSV } from "@/shared/utils/downloadAsCSV";
import LinearProgress from "@mui/material/LinearProgress";
import Fade from "@mui/material/Fade";

export interface DataTableColumn {
  name: string;
  label?: string;
  options?: {
    display?: boolean;
    download?: boolean;
    setCellHeaderProps?: () => Record<string, any>;
    customBodyRender?: (
      value: unknown,
      tableMeta?: unknown,
      updateValue?: unknown
    ) => React.ReactNode;
  };
}

export interface DataTableProps<Row = any> {
  pagination?: boolean;
  elevation?: number;
  onRowClick?: (row: Row) => void;
  download?: boolean;
  rowsPerPageOptions?: number[];
  createButton?: any;
  refetchButton?: any;
  refetchVideosButton?: any;
  repeatFromHlsButton?: any;
  repeatButton?: any;
  deleteButton?: any;
  restoreButton?: any;
  data: any[][];
  /**
   * Исходные объекты строк в том же порядке, что и `data`. Нужны там, где по
   * строке требуется решение, а не отрисовка: выбор строк и id для кнопок.
   */
  nodes?: Row[];
  /** Какие строки можно выбирать; у остальных чекбокс скрыт */
  hasShowSelect?: (row: Row) => boolean;
  /** Кнопки в тулбаре списка; функция получает id выбранных строк */
  headButtons?: React.ReactNode | ((selectedIds: string[]) => React.ReactNode);
  /** Полностью заменяет стандартные кнопки над выбранными строками */
  selectableComponent?:
    | React.ReactNode
    | ((
        selectedIds: string[],
        onClearSelection?: () => void
      ) => React.ReactNode);
  onRowsDelete?: (rows: Row[]) => void;
  selectableRows?: boolean | string;
  loading?: boolean;
  onChangePage?: (page: number) => void;
  onChangeRowsPerPage?: (rowsPerPage: number) => void;
  count?: number;
  page?: number;
  rowsPerPage?: number;
  textLabels?: any;
  title?: string;
  columns: DataTableColumn[];
  downloadButton?: any;
  serverSide?: boolean;

  [key: string]: any;

  quickFilter?: Schema;
  currentFilter?: Record<string, unknown>;
  onFilterChange?: (filter: Record<string, unknown>) => void;
}

function generateColumnsHash(columns: DataTableColumn[]): string {
  const columnNames = columns.map((col) => col.name).join("|");
  let hash = 0;
  for (let i = 0; i < columnNames.length; i++) {
    const char = columnNames.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

function getVisibleColumns(columns: DataTableColumn[]): number[] {
  const key = generateColumnsHash(columns);
  const value = localStorage.getItem(`data-table-view-columns-${key}`);

  if (value) {
    try {
      return JSON.parse(value);
    } catch {
      // ignore
    }
  }

  return columns.reduce((visibleColumns: number[], column, index) => {
    if (column.options?.display === false) return visibleColumns;
    return [...visibleColumns, index];
  }, []);
}

function saveVisibleColumns(
  columns: DataTableColumn[],
  visibleColumns: number[]
): void {
  const key = generateColumnsHash(columns);
  localStorage.setItem(
    `data-table-view-columns-${key}`,
    JSON.stringify(visibleColumns)
  );
}

export const DataTable = <Row,>({
  pagination = true,
  elevation = 0,
  onRowClick = () => {
    // noop
  },
  download = false,
  rowsPerPageOptions = [5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 200, 500],
  createButton,
  refetchButton,
  repeatFromHlsButton,
  repeatButton,
  deleteButton,
  restoreButton,
  data,
  nodes,
  hasShowSelect,
  headButtons,
  selectableComponent,
  selectableRows,
  loading,
  onChangePage,
  onChangeRowsPerPage,
  count,
  page,
  rowsPerPage,
  textLabels,
  title,
  columns,
  downloadButton,
  quickFilter,
  currentFilter,
  onFilterChange,
}: DataTableProps<Row>) => {
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<number[]>(() =>
    getVisibleColumns(columns)
  );
  const [prevDataLength, setPrevDataLength] = useState(() => data?.length || 0);

  useEffect(() => {
    const currentDataLength = data?.length || 0;

    if (currentDataLength !== prevDataLength) {
      setSelectedRows([]);
      setPrevDataLength(currentDataLength);
    } else {
      setSelectedRows((prev) => {
        const validRows = prev.filter((index) => index < currentDataLength);
        return validRows.length !== prev.length ? validRows : prev;
      });
    }
  }, [data, prevDataLength]);

  useEffect(() => {
    saveVisibleColumns(columns, visibleColumns);
  }, [columns, visibleColumns]);

  const hasData = data && data.length > 0;

  // Решить, какие строки выбираемы, можно только по исходным объектам:
  // без `nodes` считаем выбираемыми все
  const isRowSelectable = (rowIndex: number): boolean =>
    !hasShowSelect || !nodes
      ? true
      : !!nodes[rowIndex] && hasShowSelect(nodes[rowIndex]);

  const selectableRowIndexes = data
    .map((_, index) => index)
    .filter(isRowSelectable);

  const onChangeAllCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    if (selectedRows.length === selectableRowIndexes.length) {
      setSelectedRows([]);
      return;
    }
    setSelectedRows(selectableRowIndexes);
  };

  const onChangeRowCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>,
    rowIndex: number
  ) => {
    if (!isRowSelectable(rowIndex)) return;

    const newSelectedRows = [...selectedRows];
    if (event.target.checked) {
      newSelectedRows.push(rowIndex);
    } else {
      newSelectedRows.splice(newSelectedRows.indexOf(rowIndex), 1);
    }
    setSelectedRows(newSelectedRows);
  };

  const selectedIds = nodes
    ? selectedRows
        .filter((index) => nodes[index])
        .map((index) => (nodes[index] as { id?: string }).id)
        .filter((id): id is string => !!id)
    : [];

  const resolvedHeadButtons =
    typeof headButtons === "function" ? headButtons(selectedIds) : headButtons;

  const resolvedSelectableComponent =
    typeof selectableComponent === "function"
      ? selectableComponent(selectedIds, () => setSelectedRows([]))
      : selectableComponent;

  const viewColumnsButton = {
    columns,
    visibleColumns,
    setVisibleColumns,
  };

  const downloadCSVButton = {
    onClick: () => {
      const head = columns.map((column) => {
        if (column.options) {
          if ("download" in column.options) {
            if (column.options.download === false) return null;
          }
        }
        return column.name;
      });
      const body = data.map((row: any) => {
        return columns.map((column, index) => {
          if (column.options) {
            if ("download" in column.options) {
              if (column.options.download === false) return null;
            }
          }
          return row[index];
        });
      });
      const csv = [head, ...body];
      downloadAsCSV(csv, title || "data");
    },
    text: "Download CSV",
  };

  return (
    <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
      <Fade in={loading}>
        <LinearProgress />
      </Fade>
      <Box sx={{ pl: 2, pt: 1, pr: 1 }}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          {selectedRows.length > 0 ? (
            <Typography variant="h6">
              {selectedRows.length}{" "}
              {textLabels?.selected ||
                t("shared.dataTable.selected", { defaultValue: "выбрано" })}
            </Typography>
          ) : (
            <Typography variant="h6">{title}</Typography>
          )}

          <QuickFilter
            quickFilter={quickFilter}
            currentFilter={currentFilter}
            onFilterChange={onFilterChange}
          />

          {selectedRows.length > 0 ? (
            <CustomToolbarSelect
              selectedRows={selectedRows}
              deleteButton={deleteButton}
              restoreButton={restoreButton}
              repeatButton={repeatButton}
              repeatFromHlsButton={repeatFromHlsButton}
              headButtons={resolvedHeadButtons}
              selectableComponent={resolvedSelectableComponent}
            />
          ) : (
            <CustomToolbar
              createButton={createButton}
              headButtons={resolvedHeadButtons}
              refetchButton={refetchButton}
              downloadButton={
                download ? downloadCSVButton : downloadButton || undefined
              }
              viewColumnsButton={viewColumnsButton}
            />
          )}
        </Stack>
      </Box>
      <TableContainer component={Paper} elevation={elevation}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {selectableRows && (
                <TableCell padding="checkbox">
                  <Checkbox
                    color="default"
                    indeterminate={
                      selectedRows.length > 0 &&
                      selectedRows.length !== selectableRowIndexes.length
                    }
                    checked={selectedRows.length > 0}
                    disabled={selectableRowIndexes.length === 0}
                    onChange={onChangeAllCheckbox}
                  />
                </TableCell>
              )}
              {columns.map((column, index) => {
                if (!visibleColumns.includes(index)) return null;
                if (column.options?.display === false) return null;
                const setCellHeaderProps = column?.options?.setCellHeaderProps;
                const props = setCellHeaderProps && setCellHeaderProps();
                return (
                  <TableCell key={index} {...props}>
                    {column.label ?? column.name}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {hasData ? (
              data.map((row, rowIndex) => {
                const selected = selectedRows.includes(rowIndex);
                return (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      bgcolor: selected ? "background.selected" : undefined,
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                    onClick={() => onRowClick(row as any)}
                  >
                    {selectableRows && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="default"
                          checked={selected}
                          disabled={!isRowSelectable(rowIndex)}
                          sx={{ opacity: isRowSelectable(rowIndex) ? 1 : 0 }}
                          onChange={(event) =>
                            onChangeRowCheckbox(event, rowIndex)
                          }
                        />
                      </TableCell>
                    )}
                    {columns.map((column, columnIndex) => {
                      if (!visibleColumns.includes(columnIndex)) return null;
                      if (column.options?.display === false) return null;
                      const value = row[columnIndex];
                      const customBodyRender = column.options?.customBodyRender;
                      const renderedValue = customBodyRender
                        ? customBodyRender(value)
                        : value;
                      return (
                        <TableCell key={columnIndex}>{renderedValue}</TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    (selectableRows ? 1 : 0) +
                    columns.filter(
                      (_c, index) =>
                        visibleColumns.includes(index) &&
                        columns[index].options?.display !== false
                    ).length
                  }
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body2">
                    {loading
                      ? t("shared.dataTable.loading", {
                          defaultValue: "Загрузка...",
                        })
                      : t("shared.dataTable.empty", {
                          defaultValue: "Ничего не найдено",
                        })}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={count || 0}
          rowsPerPage={rowsPerPage || 10}
          page={page || 0}
          onPageChange={(_e, newPage) => onChangePage && onChangePage(newPage)}
          onRowsPerPageChange={(e) =>
            onChangeRowsPerPage && onChangeRowsPerPage(Number(e.target.value))
          }
          labelRowsPerPage={t("shared.dataTable.rowsPerPage", {
            defaultValue: "Строк на странице:",
          })}
          labelDisplayedRows={({ from, to, count }) =>
            // `total` вместо `count`: опция `count` у i18next включает
            // плюрализацию и увела бы резолв на ключи `_one`/`_other`.
            t("shared.dataTable.displayedRows", {
              defaultValue: "{{from}}-{{to}} из {{total}}",
              from,
              to,
              total: count,
            })
          }
        />
      )}
    </Box>
  );
};
